import { AttributePlainEnumValue, Image, Money } from '@commercetools/platform-sdk';
import {
  INutsProductAttributes as NutsProductAttributes,
  INutsVariantAttributes as NutsVariantAttributes,
  pivotAttributeValues,
} from '@nuts/auto-delivery-sdk/dist/utils/helpers';
import type { IsEqual } from 'type-fest';

import { PriceFragment } from '@/graphql/fragments/price';
import { findAttributeByName } from '@/lib/product/attributes';
import { getListPrice } from '@/lib/product/prices';
import { findListingImage } from '@/utils/cart';
import { ImageBySize } from '@/utils/image';
import proxiedImageUtil from '@/utils/imageProxied';
import { isDefined } from '@/utils/isDefined';
import { RawAttributeValues } from '@/utils/product';
import { reportError } from '@/utils/reportError';

type PivotedProductAttributes = Pick<
  NutsProductAttributes,
  | 'customProduct'
  | 'merchandisingCategory'
  | 'orderHint'
  | 'primaryCategory'
  | 'reportingCategory'
  | 'requiresCustomization'
>;
interface PivotedVariantAttributes
  extends Pick<
    NutsVariantAttributes,
    | 'active'
    | 'autoDeliveryEligible'
    | 'backordered'
    | 'bagSize'
    | 'bulk'
    | 'customAvailability'
    | 'sample'
    | 'variantName'
    | 'weight'
    | 'wholesale'
  > {
  pieceCost?: Money;
  variantLabel?: string;
}

/** Common attributes for Product Projection Search variants */
type PivotedAttributesCombined = PivotedProductAttributes & PivotedVariantAttributes;

/**
 * Raw attribute data to pass through {@link pivotAttributeValues}
 *
 * See `@nuts/auto-delivery-sdk/dist/utils/helpers`
 */
export type ProductProjectionVariantAttributesRaw = RawAttributeValues<
  Omit<PivotedAttributesCombined, 'backordered' | 'variantLabel'> & {
    backordered: AttributePlainEnumValue;
    variantLabel?: AttributePlainEnumValue;
  }
>;

export interface ProductProjectionResultVariant {
  attributesRaw: ProductProjectionVariantAttributesRaw;
  id: number;
  images?: Pick<Image, 'label' | 'url'>[];
  prices: PriceFragment[];
  sku: string;
}

export interface WithAllVariants {
  allVariants: ProductProjectionResultVariant[];
}

export interface WithMasterVariant {
  masterVariant: ProductProjectionResultVariant;
}

export interface BaseProductProjectionResult {
  id: string;
  key: string;
  name: string;
}

/**
 * Common attributes for Product Projection Search results.
 *
 * To be used when building product lists, for a consistent shape across the application. Modeled
 * after the `ProductCardData` concept.
 *
 * To distinguish between `WithAllVariants` and `WithMasterVariant`, must be combined with the TS
 * helper `Extract<T>`. E.g. `Extract<ProductProjectionResult, WithAllVariants>`.
 *
 * Current use cases do not need to construct a product path. When this becomes necessary, we can
 * abstract the behavior in `@/api/productPathsById` and `@/graphql/productPathsById`.
 */
export type ProductProjectionResult = (WithAllVariants | WithMasterVariant) &
  BaseProductProjectionResult;

export interface NutsProductProjectionVariant
  extends Omit<ProductProjectionResultVariant, 'attributesRaw' | 'images'>,
    PivotedVariantAttributes {
  listImage: ImageBySize;
  listPrice: PriceFragment;
  masterVariantId: number;
}

export interface NutsProductProjection
  extends Pick<ProductProjectionResult, 'name'>,
    PivotedProductAttributes {
  productId: ProductProjectionResult['id'];
  productKey: ProductProjectionResult['key'];
  variants: NutsProductProjectionVariant[];
}

export interface NutsProductProjectionDisplayVariant
  extends Omit<NutsProductProjection, 'variants'> {
  variant: NutsProductProjectionVariant;
}

export const NutsProductProjection = {
  /**
   * Use of `allVariants` presumes that the master variant will not be filtered out by the query.
   */
  fromQueryResult(
    result: ProductProjectionResult,
    options?: {
      skipInvalidVariants?: boolean;
    },
  ): NutsProductProjection {
    const rawMasterVariant = 'allVariants' in result ? result.allVariants[0] : result.masterVariant;
    const resultVariants = 'allVariants' in result ? result.allVariants : [result.masterVariant];
    const { product: pivotedProductAttributes } = pivotAttributeValues(
      rawMasterVariant.attributesRaw,
    );

    const variants: NutsProductProjectionVariant[] = [];
    resultVariants.forEach((projectionResultVariant) => {
      const { attributesRaw, images, ...variant } = projectionResultVariant;

      const attributes = pivotAttributeValues(attributesRaw);
      const image = findListingImage(mergeImages(images, rawMasterVariant.images));

      let listPrice: PriceFragment | undefined;
      try {
        listPrice = getListPrice(variant.prices);
      } catch {
        // handled below
      }

      if (!image || !listPrice) {
        const missingKeys = Object.entries({ listImage: image, listPrice })
          .filter(([_, value]) => !value)
          .map(([key]) => key)
          .join(', ');
        const message = `Failed to build NutsProductProjection for ${variant.sku}; missing ${missingKeys}`;
        const error = new Error(message);
        if (options?.skipInvalidVariants) {
          reportError(error);
          return;
        }
        throw error;
      }

      const [listImage] = proxiedImageUtil.getVariants([image]);
      variants.push({
        ...variant,
        ...attributes.variant,
        listImage,
        listPrice,
        masterVariantId: rawMasterVariant.id,
        pieceCost: findAttributeByName(attributesRaw, 'pieceCost'), // not pivoted by AD SDK
        variantLabel: findAttributeByName(attributesRaw, 'variantLabel')?.key,
      });
    });

    return {
      ...pivotedProductAttributes,
      name: result.name,
      productId: result.id,
      productKey: result.key,
      variants,
    };
  },

  toDisplayVariant(
    projection: NutsProductProjection,
    options?: {
      sku?: string;
      variantId?: number;
    },
  ): NutsProductProjectionDisplayVariant {
    let variant: NutsProductProjectionVariant | undefined;

    if (options?.sku) {
      // Look up by SKU
      variant = projection.variants.find((v) => v.sku === options.sku);
      if (!variant) {
        throw new Error(`Variant with SKU ${options.sku} not found in ${projection.productKey}`);
      }
    } else {
      // Look up by variant ID (or use first variant if undefined)
      const targetId = options?.variantId ?? projection.variants[0].masterVariantId;
      variant = projection.variants.find((v) => v.id === targetId);
      if (!variant) {
        throw new Error(`Variant ${targetId} not found in ${projection.productKey}`);
      }
    }

    const { variants, ...product } = projection;

    return {
      ...product,
      variant,
    };
  },
};

function mergeImages(...images: ProductProjectionResultVariant['images'][]) {
  return images.flat().filter(isDefined);
}

export const attributeNames = [
  'active',
  'autoDeliveryEligible',
  'backordered',
  'bagSize',
  'bulk',
  'customAvailability',
  'customProduct',
  'merchandisingCategory',
  'orderHint',
  'pieceCost',
  'primaryCategory',
  'reportingCategory',
  'requiresCustomization',
  'sample',
  'variantLabel',
  'variantName',
  'weight',
  'wholesale',
] as const;

type AttributeNames = (typeof attributeNames)[number];
type KeysMatch = IsEqual<AttributeNames, keyof PivotedAttributesCombined>;

/**
 * Force the constraint to be checked at compile time with a direct value assignment.
 * If you comment out any key above or add an extra one, this line will trigger a
 * type error: "Type 'true' does not satisfy the constraint 'never'"
 */
export const ENSURE_KEYS_MATCH: KeysMatch extends true ? true : never = true;
